import { AlertCircle, ChevronLeft, ChevronRight, CreditCard, List } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useActiveContract } from '@/features/auth/hooks/useActiveContract'
import { useBollette } from '@/lib/graphql/hooks'
import { DownloadSection } from './components/DownloadSection'
import { InvoiceHeader } from './components/InvoiceHeader'
import { InvoiceInfoRow } from './components/InvoiceInfoRow'
import { PaymentMethodCard } from './components/PaymentMethodCard'
import { VociDonut } from './components/VociDonut'
import { VociList } from './components/VociList'
import { checkCanPay } from './constants'

export function InvoiceDetailPage() {
  const { codice } = useParams<{ codice: string }>()
  const { cliente, activeContract } = useActiveContract()

  const codiceCliente = cliente?.codice_cliente
  const codiceContratto = activeContract?.codice_contratto

  const { data: bollette, isLoading, error } = useBollette(codiceCliente, codiceContratto)

  const invoice = bollette?.find((b) => b.codice === codice)
  const metodo = activeContract?.metodo_pagamento ?? null
  const canPay = checkCanPay(invoice ?? null, metodo)

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Link
        to="/bollette"
        className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-secondary hover:underline"
      >
        <ChevronLeft className="h-4 w-4" />
        Bollette
      </Link>

      {error ? (
        <EmptyState
          icon={<AlertCircle className="h-5 w-5 text-destructive" />}
          title="Non siamo riusciti a caricare la bolletta"
          description={error.message}
        />
      ) : isLoading ? (
        <DetailSkeleton />
      ) : !invoice ? (
        <EmptyState
          title="Bolletta non trovata"
          description="Potrebbe essere stata rimossa o non appartiene al contratto selezionato."
        />
      ) : (
        <>
          <InvoiceHeader invoice={invoice} />
          <InvoiceInfoRow invoice={invoice} />
          <DownloadSection invoice={invoice} />

          <section className="flex flex-col gap-2">
            <h2 className="text-base font-bold text-foreground">Dettaglio voci</h2>
            <VociList invoice={invoice} />
          </section>

          <Card className="flex items-center justify-center p-6">
            <VociDonut invoice={invoice} />
          </Card>

          <Link
            to={`/bollette/${encodeURIComponent(codice ?? '')}/voci`}
            className="flex h-12 min-h-12 w-full items-center justify-between gap-3 rounded-xl bg-primary px-5 text-sm font-bold uppercase leading-none tracking-wide text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <span className="inline-flex items-center gap-2">
              <List className="h-4 w-4" />
              Dettaglio voci per periodo
            </span>
            <ChevronRight className="h-4 w-4" />
          </Link>

          <PaymentMethodCard metodo={metodo} />

          {canPay && (
            <Link
              to={`/pagamenti/${encodeURIComponent(invoice.codice)}`}
              className="flex h-14 min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-success text-base font-bold leading-none text-success-foreground transition-colors hover:bg-success/90"
            >
              <CreditCard className="h-5 w-5" />
              Paga {Number(invoice.imponibile || 0).toFixed(2).replace('.', ',')} €
            </Link>
          )}
        </>
      )}
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-48" />
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
      <Skeleton className="h-40 rounded-xl" />
      <Skeleton className="h-64 rounded-xl" />
      <Skeleton className="h-12 rounded-xl" />
    </div>
  )
}
